import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Shield, Users, Key, FileText, Settings, 
  RefreshCw, Eye, EyeOff, CheckCircle, XCircle,
  MessageSquare, Mail, BookOpen, ExternalLink, Flag, DatabaseZap, Database, LibraryBig
} from "lucide-react";
import { AdminsTab, RolesTab, AuditTab, TicketsTab, SettingsTab } from "../components/admin/tabs";
import EmailPreviewTab from "../components/admin/tabs/EmailPreviewTab";
import DataMigrationTab from "../components/admin/tabs/DataMigrationTab";
import RecordsTab from "../components/admin/tabs/RecordsTab";
import CurriculumAuthoringTab from "../components/admin/tabs/CurriculumAuthoringTab";
import ContentEditorTab from "../components/admin/tabs/ContentEditorTab";
import AdminFeatureFlags from "./AdminFeatureFlags";


import { useAdminEnv, ReadOnlyBanner } from "../components/admin/EnvContext";

// Helper functions for ticket display
const getTicketPriorityColor = (priority) => {
  const colors = {
    low: "bg-gray-100 text-gray-700",
    medium: "bg-yellow-100 text-yellow-700",
    high: "bg-orange-100 text-orange-700",
    urgent: "bg-red-100 text-red-700"
  };
  return colors[priority] || colors.medium;
};

const getTicketStatusColor = (status) => {
  const colors = {
    open: "bg-blue-100 text-blue-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    waiting_on_user: "bg-purple-100 text-purple-700",
    resolved: "bg-green-100 text-green-700",
    closed: "bg-gray-100 text-gray-700"
  };
  return colors[status] || colors.open;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function AdminConsole() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isProduction } = useAdminEnv();
  const section = location.pathname.split("/").filter(Boolean).pop();
  const activeTab = section || "admins";
  const controlPlane = true;
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  
  // Admin data
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Support tickets data
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketReply, setTicketReply] = useState("");
  
  // Settings data
  const [settings, setSettings] = useState([]);
  const [settingsGrouped, setSettingsGrouped] = useState({});
  
  // Modal states
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [showViewRoleModal, setShowViewRoleModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({ email: "", name: "", password: "", role: "admin", mobile: "", grades: [], subjects: [] });
  const [editForm, setEditForm] = useState({ email: "", name: "", role: "", active: true, reason: "", grades: [], subjects: [] });
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Audit log filters
  const [auditFilters, setAuditFilters] = useState({
    action_category: "",
    admin_id: "",
    actor_type: "",
    date_from: "",
    date_to: ""
  });
  const [auditPagination, setAuditPagination] = useState({ skip: 0, limit: 20, total: 0 });

  // Authentication is handled via HttpOnly cookies - no localStorage needed
  const fetchConsoleData = useCallback(async () => {
    try {
      const [adminsRes, rolesRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/console/admins`, { credentials: "include" }),
        fetch(`${API}/admin/console/roles`, { credentials: "include" }),
        fetch(`${API}/admin/console/stats`, { credentials: "include" })
      ]);
      
      if (adminsRes.status === 403) {
        toast.error("Access denied. Super Admin privileges required.");
        navigate("/admin/dashboard");
        return;
      }
      
      if (adminsRes.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/admin");
        return;
      }
      
      // Parse responses with error handling
      let adminsData = {}, rolesData = {}, statsData = {};
      try {
        adminsData = await adminsRes.json();
      } catch (e) { console.warn("Failed to parse admins response"); }
      try {
        rolesData = await rolesRes.json();
      } catch (e) { console.warn("Failed to parse roles response"); }
      try {
        statsData = await statsRes.json();
      } catch (e) { console.warn("Failed to parse stats response"); }
      
      if (adminsData.success) setAdmins(adminsData.admins);
      if (rolesData.success) setRoles(rolesData.roles);
      if (statsData.success) setStats(statsData.stats);
      
    } catch (error) {
      console.error("Console fetch error:", error);
      toast.error("Failed to load admin console");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        skip: auditPagination.skip.toString(),
        limit: auditPagination.limit.toString(),
        ...(auditFilters.action_category && { action_category: auditFilters.action_category }),
        ...(auditFilters.admin_id && { admin_id: auditFilters.admin_id }),
        ...(auditFilters.actor_type && { actor_type: auditFilters.actor_type }),
        ...(auditFilters.date_from && { date_from: auditFilters.date_from }),
        ...(auditFilters.date_to && { date_to: auditFilters.date_to })
      });
      
      const res = await fetch(`${API}/admin/console/audit-logs?${params}`, { 
        credentials: "include" 
      });
      
      const data = await res.json();
      if (data.success) {
        setAuditLogs(data.logs);
        setAuditPagination(prev => ({ ...prev, total: data.total }));
      }
    } catch (error) {
      console.error("Audit logs fetch error:", error);
    }
  }, [auditPagination.skip, auditPagination.limit, auditFilters]);

  const fetchTickets = useCallback(async () => {
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/tickets`, { credentials: "include" }),
        fetch(`${API}/admin/tickets/stats`, { credentials: "include" })
      ]);
      
      const ticketsData = await ticketsRes.json();
      const statsData = await statsRes.json();
      
      if (ticketsData.success) setTickets(ticketsData.tickets);
      if (statsData.success) setTicketStats(statsData.stats);
    } catch (error) {
      console.error("Tickets fetch error:", error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/settings`, { credentials: "include" });
      const data = await res.json();
      
      if (data.success) {
        setSettings(data.settings);
        setSettingsGrouped(data.grouped);
      }
    } catch (error) {
      console.error("Settings fetch error:", error);
    }
  }, []);

  // Auth check - verify session via HttpOnly cookies
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API}/admin/auth/me`, {
          credentials: "include"
        });
        
        if (!res.ok) {
          toast.error("Session expired. Please login again.");
          navigate("/admin");
          return;
        }
        
        // Auth valid, proceed to load data
        fetchConsoleData();
        fetchAuditLogs();
        fetchTickets();
        fetchSettings();
      } catch (error) {
        console.error("Auth check error:", error);
        navigate("/admin");
      }
    };
    
    checkAuth();
  }, [navigate, fetchConsoleData, fetchAuditLogs, fetchTickets, fetchSettings]);

  useEffect(() => {
    if (activeTab === "audit") {
      fetchAuditLogs();
    } else if (activeTab === "tickets") {
      fetchTickets();
    } else if (activeTab === "settings") {
      fetchSettings();
    }
  }, [activeTab, fetchAuditLogs, fetchTickets, fetchSettings]);

  const handleCreateAdmin = async () => {
    if (!createForm.email || !createForm.name) {
      toast.error("Please enter the user's name and email");
      return;
    }
    const isCM = createForm.role === "content_manager";
    if (isCM) {
      if (!createForm.mobile.trim()) { toast.error("Mobile number is required for a Content Manager"); return; }
      if (!createForm.grades.length || !createForm.subjects.length) {
        toast.error("Assign at least one class and one subject"); return;
      }
    }
    setFormLoading(true);
    try {
      const url = isCM ? `${API}/admin/content-managers` : `${API}/admin/console/admins/invite`;
      const body = isCM
        ? { email: createForm.email, name: createForm.name, mobile: createForm.mobile,
            grades: createForm.grades, subjects: createForm.subjects }
        : { email: createForm.email, name: createForm.name, role: createForm.role, base_url: window.location.origin };
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(isCM
          ? (data.email_sent ? "Content Manager invited — activation email sent" : "Content Manager created (email failed; check logs)")
          : (data.message || "Invitation sent"));
        setShowCreateAdminModal(false);
        setCreateForm({ email: "", name: "", password: "", role: "admin", mobile: "", grades: [], subjects: [] });
        fetchConsoleData();
      } else {
        toast.error(data.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setFormLoading(false);
    }
  };

  const handleResendInvite = async (admin) => {
    try {
      const res = await fetch(`${API}/admin/console/admins/${admin.id}/resend-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ base_url: window.location.origin })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Invitation re-sent to ${admin.email}`);
        fetchConsoleData();
      } else {
        toast.error(data.detail || "Failed to resend invitation");
      }
    } catch (error) {
      toast.error("Failed to resend invitation");
    }
  };

  const handleReactivateAdmin = async (admin) => {
    try {
      const res = await fetch(`${API}/admin/console/admins/${admin.id}/reactivate`, {
        method: "POST",
        credentials: "include"
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || "User reactivated");
        fetchConsoleData();
      } else {
        toast.error(data.detail || "Failed to reactivate user");
      }
    } catch (error) {
      toast.error("Failed to reactivate user");
    }
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;
    const isCM = editForm.role === "content_manager";
    if (isCM && (!editForm.grades.length || !editForm.subjects.length)) {
      toast.error("Assign at least one class and one subject");
      return;
    }
    setFormLoading(true);
    try {
      const res = await fetch(`${API}/admin/console/admins/${selectedAdmin.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editForm)
      });
      
      const data = await res.json();
      if (data.success) {
        if (isCM) {
          const sres = await fetch(`${API}/admin/content-managers/${selectedAdmin.id}/scope`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ grades: editForm.grades, subjects: editForm.subjects })
          });
          const sdata = await sres.json().catch(() => ({}));
          if (!sres.ok || !sdata.success) {
            toast.error(sdata.detail || "User saved, but updating the content scope failed");
          }
        }
        toast.success("User updated successfully");
        setShowEditAdminModal(false);
        setSelectedAdmin(null);
        fetchConsoleData();
      } else {
        toast.error(data.detail || "Failed to update admin");
      }
    } catch (error) {
      toast.error("Failed to update admin");
    } finally {
      setFormLoading(false);
    }
  };

  const handleChangeRole = async (admin, newRole) => {
    try {
      const res = await fetch(`${API}/admin/console/admins/${admin.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole, reason: "Inline role change from Users tab" })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Role updated to ${newRole.replace(/_/g, " ")}`);
        fetchConsoleData();
      } else {
        toast.error(data.detail || "Failed to change role");
      }
    } catch (error) {
      toast.error("Failed to change role");
    }
  };

  const handleDeactivateAdmin = async (admin) => {
    if (!window.confirm(`Are you sure you want to deactivate ${admin.email}?`)) return;
    
    try {
      const res = await fetch(`${API}/admin/console/admins/${admin.id}`, {
        method: "DELETE",
        
        credentials: "include"
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Admin deactivated");
        fetchConsoleData();
      } else {
        toast.error(data.detail || "Failed to deactivate admin");
      }
    } catch (error) {
      toast.error("Failed to deactivate admin");
    }
  };

  const handleReset2FA = async (admin) => {
    if (!window.confirm(`Reset 2FA for ${admin.email}?`)) return;
    
    try {
      const res = await fetch(`${API}/admin/console/admins/${admin.id}/reset-2fa`, {
        method: "POST",
        
        credentials: "include"
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("2FA reset successfully");
      } else {
        toast.error(data.detail || "Failed to reset 2FA");
      }
    } catch (error) {
      toast.error("Failed to reset 2FA");
    }
  };

  const exportAuditLogs = async () => {
    try {
      const res = await fetch(`${API}/admin/console/audit-logs/export`, { 
        credentials: "include" 
      });
      
      const data = await res.json();
      if (data.success) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        toast.success("Audit logs exported");
      }
    } catch (error) {
      toast.error("Failed to export audit logs");
    }
  };

  // Ticket handlers
  const handleTicketReply = async () => {
    if (!selectedTicket || !ticketReply.trim()) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API}/admin/tickets/${selectedTicket.id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: ticketReply })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Reply sent");
        setTicketReply("");
        fetchTickets();
        // Refresh selected ticket
        const updatedRes = await fetch(`${API}/admin/tickets/${selectedTicket.id}`, {
          
          credentials: "include"
        });
        const updatedData = await updatedRes.json();
        if (updatedData.success) setSelectedTicket(updatedData.ticket);
      } else {
        toast.error(data.detail || "Failed to send reply");
      }
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      const res = await fetch(`${API}/admin/tickets/${ticketId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Ticket updated");
        fetchTickets();
      }
    } catch (error) {
      toast.error("Failed to update ticket");
    }
  };

  // Settings handlers
  const handleToggleSetting = async (key, currentValue) => {
    try {
      const res = await fetch(`${API}/admin/settings/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value: !currentValue })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Setting updated");
        fetchSettings();
      } else {
        toast.error(data.detail || "Failed to update setting");
      }
    } catch (error) {
      toast.error("Failed to update setting");
    }
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      const res = await fetch(`${API}/admin/settings/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value: parseFloat(value) || value })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Setting updated");
        fetchSettings();
      } else {
        toast.error(data.detail || "Failed to update setting");
      }
    } catch (error) {
      toast.error("Failed to update setting");
    }
  };

  // Helper functions moved to tab components

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-console-page">
        <Tabs value={activeTab} className="space-y-6" data-testid="admin-console-tabs">
          <TabsList className="hidden">
            <TabsTrigger value="admins" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-admins">
              <Users className="h-4 w-4 mr-2" />
              Admins
            </TabsTrigger>
            <TabsTrigger value="roles" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-roles">
              <Key className="h-4 w-4 mr-2" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="tickets" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-tickets">
              <MessageSquare className="h-4 w-4 mr-2" />
              Tickets
              {ticketStats?.open > 0 && (
                <Badge className="ml-2 bg-amber-500 text-white">{ticketStats.open}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-audit">
              <FileText className="h-4 w-4 mr-2" />
              Audit Logs
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-emails">
              <Mail className="h-4 w-4 mr-2" />
              Email Templates
            </TabsTrigger>
            {controlPlane && (
              <TabsTrigger
                value="curriculum"
                className="data-[state=active]:bg-red-500 data-[state=active]:text-white"
                data-testid="tab-curriculum"
              >
                <LibraryBig className="h-4 w-4 mr-2" />
                Curriculum
              </TabsTrigger>
            )}
            {controlPlane && (
              <TabsTrigger
                value="content-editor"
                className="data-[state=active]:bg-red-500 data-[state=active]:text-white"
                data-testid="tab-content-editor"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Content Editor
              </TabsTrigger>
            )}
            {controlPlane && (
              <TabsTrigger value="migration" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-migration">
                <DatabaseZap className="h-4 w-4 mr-2" />
                Data Migration
              </TabsTrigger>
            )}
            {controlPlane && (
              <TabsTrigger value="records" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-records">
                <Database className="h-4 w-4 mr-2" />
                Records
              </TabsTrigger>
            )}
          </TabsList>

          {/* Admins Tab */}
          <TabsContent value="admins" className="space-y-4">
            <AdminsTab
              admins={admins}
              roles={roles}
              onChangeRole={handleChangeRole}
              onCreateAdmin={() => setShowCreateAdminModal(true)}
              onEditAdmin={(admin) => {
                setSelectedAdmin(admin);
                setEditForm({
                  email: admin.email,
                  name: admin.name,
                  role: admin.role,
                  active: admin.active,
                  reason: "",
                  grades: admin.content_scope?.grades || [],
                  subjects: admin.content_scope?.subjects || []
                });
                setShowEditAdminModal(true);
              }}
              onDeactivateAdmin={handleDeactivateAdmin}
              onReactivateAdmin={handleReactivateAdmin}
              onResendInvite={handleResendInvite}
              onReset2FA={handleReset2FA}
            />
          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles" className="space-y-4">
            {isProduction && <ReadOnlyBanner feature="Roles & Permissions" />}
            <RolesTab
              roles={roles}
              readOnly={isProduction}
              onViewRole={(role) => {
                setSelectedRole(role);
                setShowViewRoleModal(true);
              }}
            />
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit" className="space-y-4">
            <AuditTab
              auditLogs={auditLogs}
              auditFilters={auditFilters}
              auditPagination={auditPagination}
              onFilterChange={(field, value) => setAuditFilters(prev => ({ ...prev, [field]: value }))}
              onApplyFilters={fetchAuditLogs}
              onExport={exportAuditLogs}
              onPaginationChange={(newSkip) => setAuditPagination(prev => ({ ...prev, skip: newSkip }))}
            />
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="tickets" className="space-y-4">
            <TicketsTab
              tickets={tickets}
              ticketStats={ticketStats}
              onViewTicket={async (ticket) => {
                const res = await fetch(`${API}/api/admin/tickets/${ticket.id}`, {
                  credentials: "include"
                });
                const data = await res.json();
                if (data.success) {
                  setSelectedTicket(data.ticket);
                  setShowTicketModal(true);
                }
              }}
              onResolveTicket={(ticketId) => handleUpdateTicketStatus(ticketId, "resolved")}
            />
          </TabsContent>

          {/* System Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <SettingsTab
              settingsGrouped={settingsGrouped}
              onToggleSetting={handleToggleSetting}
              onUpdateSetting={handleUpdateSetting}
            />
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="emails" className="space-y-4">
            {isProduction && <ReadOnlyBanner feature="Email Templates" />}
            <EmailPreviewTab readOnly={isProduction} />
          </TabsContent>

          {controlPlane && (
            <TabsContent value="curriculum" className="space-y-4">
              {isProduction && <ReadOnlyBanner feature="Curriculum authoring" />}
              <CurriculumAuthoringTab readOnly={isProduction} />
            </TabsContent>
          )}

          {controlPlane && (
            <TabsContent value="content-editor" className="space-y-4">
              {isProduction && <ReadOnlyBanner feature="The Content Editor" />}
              <ContentEditorTab readOnly={isProduction} />
            </TabsContent>
          )}

          {/* Content Managers consolidated into the Users tab — provision via the Create User flow (content-authoring role) */}

          <TabsContent value="migration" className="space-y-4">
            {isProduction && <ReadOnlyBanner feature="Data Migration (push & mirror)" />}
            <DataMigrationTab readOnly={isProduction} />
          </TabsContent>

          <TabsContent value="records" className="space-y-4">
            <RecordsTab />
          </TabsContent>
        </Tabs>

      {/* Create Admin Modal */}
      <Dialog open={showCreateAdminModal} onOpenChange={setShowCreateAdminModal}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Invite New User</DialogTitle>
            <DialogDescription className="text-gray-500">
              They'll get an email to set their own password and activate their account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 font-medium">Name</label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 font-medium">Email</label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@example.com"
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 font-medium">Role</label>
              <select
                value={createForm.role}
                onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                className="w-full p-2 rounded-md bg-gray-50 border border-gray-200 text-gray-900"
              >
                {roles.filter(r => r.value !== "super_admin").map(r => (
                  <option key={r.value} value={r.value}>
                    {r.name}{r.is_system_role ? "" : " (Custom)"}
                  </option>
                ))}
              </select>
            </div>

            {createForm.role === "content_manager" && (
              <div className="space-y-4 rounded-lg border border-purple-200 bg-purple-50/50 p-3" data-testid="cm-scope-fields">
                <p className="text-xs text-purple-700">
                  Content Managers sign in via the Content Manager portal and can only manage the classes &amp; subjects you assign below. Pick multiple as needed.
                </p>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Mobile</label>
                  <Input
                    value={createForm.mobile}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, mobile: e.target.value }))}
                    placeholder="e.g. 9876543210"
                    className="bg-white border-gray-200"
                    data-testid="cm-mobile-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Classes</label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="cm-grades">
                    {[5, 6, 7, 8].map((g) => {
                      const on = createForm.grades.includes(g);
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setCreateForm(prev => ({
                            ...prev,
                            grades: on ? prev.grades.filter(x => x !== g) : [...prev.grades, g]
                          }))}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200"}`}
                          data-testid={`cm-grade-${g}`}
                        >
                          Class {g}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Subjects</label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="cm-subjects">
                    {[["math", "Math"], ["science", "Science"], ["english", "English Grammar"], ["hindi", "Hindi Vyakaran"]].map(([val, label]) => {
                      const on = createForm.subjects.includes(val);
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setCreateForm(prev => ({
                            ...prev,
                            subjects: on ? prev.subjects.filter(x => x !== val) : [...prev.subjects, val]
                          }))}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200"}`}
                          data-testid={`cm-subject-${val}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdminModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button onClick={handleCreateAdmin} disabled={formLoading} className="bg-red-500 hover:bg-red-600 text-white" data-testid="send-invite-btn">
              {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Modal */}
      <Dialog open={showEditAdminModal} onOpenChange={setShowEditAdminModal}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Edit Admin</DialogTitle>
            <DialogDescription className="text-gray-500">
              Update admin details and role. Changes will be logged.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 font-medium">Name</label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 font-medium">Email</label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 font-medium">Role</label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                className="w-full p-2 rounded-md bg-gray-50 border border-gray-200 text-gray-900"
                disabled={selectedAdmin?.email === "admin@contentiumtech.com"}
              >
                {roles.map(r => (
                  <option key={r.value} value={r.value}>
                    {r.name}{r.is_system_role ? "" : " (Custom)"}
                  </option>
                ))}
              </select>
            </div>

            {editForm.role === "content_manager" && (
              <div className="space-y-4 rounded-lg border border-purple-200 bg-purple-50/50 p-3" data-testid="cm-edit-scope-fields">
                <p className="text-xs text-purple-700">Classes &amp; subjects this Content Manager can manage. Pick multiple as needed.</p>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Classes</label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="cm-edit-grades">
                    {[5, 6, 7, 8].map((g) => {
                      const on = editForm.grades.includes(g);
                      return (
                        <button key={g} type="button"
                          onClick={() => setEditForm(prev => ({ ...prev, grades: on ? prev.grades.filter(x => x !== g) : [...prev.grades, g] }))}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200"}`}
                          data-testid={`cm-edit-grade-${g}`}>
                          Class {g}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Subjects</label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="cm-edit-subjects">
                    {[["math", "Math"], ["science", "Science"], ["english", "English Grammar"], ["hindi", "Hindi Vyakaran"]].map(([val, label]) => {
                      const on = editForm.subjects.includes(val);
                      return (
                        <button key={val} type="button"
                          onClick={() => setEditForm(prev => ({ ...prev, subjects: on ? prev.subjects.filter(x => x !== val) : [...prev.subjects, val] }))}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200"}`}
                          data-testid={`cm-edit-subject-${val}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.active}
                onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
                className="rounded border-gray-300"
                disabled={selectedAdmin?.email === "admin@contentiumtech.com"}
              />
              <label className="text-sm text-gray-600">Active</label>
            </div>
            <div>
              <label className="text-sm text-gray-600 font-medium">Reason for change (optional)</label>
              <Input
                value={editForm.reason}
                onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Document reason for audit"
                className="bg-gray-50 border-gray-200"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditAdminModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button onClick={handleUpdateAdmin} disabled={formLoading} className="bg-red-500 hover:bg-red-600 text-white">
              {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              {selectedTicket?.ticket_number}
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              {selectedTicket?.subject}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Ticket Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="text-xs text-gray-500">Parent</p>
                  <p className="text-sm text-gray-900 font-medium">{selectedTicket.parent_name}</p>
                  <p className="text-xs text-gray-500">{selectedTicket.parent_email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Category</p>
                  <Badge variant="outline" className="border-gray-300 text-gray-700">
                    {selectedTicket.category}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Priority</p>
                  <Badge className={getTicketPriorityColor(selectedTicket.priority)}>
                    {selectedTicket.priority}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={getTicketStatusColor(selectedTicket.status)}>
                    {selectedTicket.status?.replace("_", " ")}
                  </Badge>
                </div>
              </div>
              
              {/* Messages */}
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      msg.sender_type === "admin"
                        ? "bg-blue-50 border border-blue-200 ml-4"
                        : "bg-gray-50 border border-gray-200 mr-4"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-medium text-gray-700">
                        {msg.sender_name} ({msg.sender_type})
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>
              
              {/* Reply Input */}
              {selectedTicket.status !== "closed" && (
                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <textarea
                    value={ticketReply}
                    onChange={(e) => setTicketReply(e.target.value)}
                    placeholder="Type your reply..."
                    className="w-full p-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 resize-none"
                    rows={3}
                  />
                  <div className="flex justify-between">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateTicketStatus(selectedTicket.id, "in_progress")}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      >
                        Mark In Progress
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateTicketStatus(selectedTicket.id, "resolved")}
                        className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                      >
                        Mark Resolved
                      </Button>
                    </div>
                    <Button
                      onClick={handleTicketReply}
                      disabled={!ticketReply.trim() || formLoading}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Send Reply"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketModal(false)} className="border-gray-200">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Role Modal */}
      <Dialog open={showViewRoleModal} onOpenChange={setShowViewRoleModal}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900">{selectedRole?.name} Permissions</DialogTitle>
            <DialogDescription className="text-gray-500">
              This role has {selectedRole?.permission_count} permissions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              {selectedRole?.permissions?.map((perm) => (
                <div key={perm} className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm text-gray-700">{perm}</span>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewRoleModal(false)} className="border-gray-200">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
